import { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Spinner,
  Dropdown,
  Option,
  OptionGroup,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import type { Planner } from '@/types/planner.types';
import { useHandoffDialogStyles } from './HandoffDialog.styles';

export interface HandoffDialogProps {
  open: boolean;
  eventCount: number;
  planners: Planner[];
  selectedPlanner: Planner | null;
  onPlannerChange: (planner: Planner | null) => void;
  isProcessing: boolean;
  isLoadingPlanners?: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const HandoffDialog: React.FC<HandoffDialogProps> = ({
  open,
  eventCount,
  planners,
  selectedPlanner,
  onPlannerChange,
  isProcessing,
  isLoadingPlanners = false,
  error,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('planner');
  const styles = useHandoffDialogStyles();

  const handleDropdownChange = useCallback(
    (_event: unknown, data: { optionValue?: string }) => {
      if (!data.optionValue) {
        onPlannerChange(null);
        return;
      }
      const planner = planners.find((p) => p.id === data.optionValue) ?? null;
      onPlannerChange(planner);
    },
    [planners, onPlannerChange]
  );

  const isEmpty = planners.length === 0;
  const sortedPlanners = useMemo(
    () => [...planners].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [planners]
  );

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && !isProcessing && onCancel()}>
      <DialogSurface
        aria-describedby="handoff-dialog-body"
        style={{ width: '37.5rem', borderRadius: '0.5rem' }}
      >
        <DialogBody>
          <DialogTitle>{t('dialogs.handoff.title')}</DialogTitle>
          <DialogContent id="handoff-dialog-body">
            <p>{t('dialogs.handoff.body', { count: eventCount })}</p>
            {isLoadingPlanners ? (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '0.5rem' }}
              >
                <Spinner size="tiny" />
                <span>{t('dialogs.handoff.loadingPlanners')}</span>
              </div>
            ) : (
              <Dropdown
                placeholder={
                  isEmpty ? t('dialogs.handoff.emptyPlanners') : t('dialogs.handoff.selectPlanner')
                }
                aria-label={t('dialogs.handoff.selectPlanner')}
                value={selectedPlanner?.displayName ?? ''}
                selectedOptions={selectedPlanner ? [selectedPlanner.id] : []}
                onOptionSelect={handleDropdownChange}
                disabled={isProcessing || isEmpty}
                data-testid="handoff-planner-dropdown"
                style={{ width: '100%', marginTop: '0.5rem' }}
                listbox={{ className: styles.plannerListbox }}
              >
                {!isEmpty && (
                  <OptionGroup label={t('dialogs.handoff.plannerListHeader')}>
                    {sortedPlanners.map((planner) => (
                      <Option
                        key={planner.id}
                        value={planner.id}
                        data-testid={`planner-option-${planner.id}`}
                      >
                        {planner.displayName}
                      </Option>
                    ))}
                  </OptionGroup>
                )}
              </Dropdown>
            )}
            {error && (
              <p
                role="alert"
                style={{ color: 'var(--colorPaletteRedForeground1)', marginTop: '0.5rem' }}
              >
                {error}
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <BasicButton
              variant="primary"
              size="medium"
              onClick={onConfirm}
              disabled={isProcessing || !selectedPlanner}
              icon={isProcessing ? <Spinner size="tiny" /> : undefined}
              data-testid="handoff-dialog-confirm"
            >
              {isProcessing ? t('dialogs.handoff.processing') : t('dialogs.handoff.confirm')}
            </BasicButton>
            <BasicButton
              variant="secondary"
              size="medium"
              onClick={onCancel}
              disabled={isProcessing}
              data-testid="handoff-dialog-cancel"
            >
              {t('dialogs.handoff.cancel')}
            </BasicButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

HandoffDialog.displayName = 'HandoffDialog';
